import type { Auth, User } from "firebase/auth";
import { memberName } from "./memberNames";
import { signOut } from "./signOut";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { callDay, callTime, emailName, shortTime } from "./calls";
import { LEAD_COLUMNS } from "./columns";
import { LeadDeal } from "./DealPages";
import { DeletedBanner, DeleteRecord } from "./DeleteRecord";
import { deletedTitle } from "./deletion";
import { LeadFollowUps } from "./FollowUpPages";
import { go, useCrumb, useTitle } from "./hooks";
import { LeadImport } from "./LeadImport";
import { LocationCard } from "./LocationCard";
import { leadPinFilters } from "./mapView";
import { PinMap, useMapMode, ViewSwitch } from "./PinMap";
import { navigate } from "./Menu";
import {
  defaultLeadView,
  forgetCallNote,
  type LeadField,
  type LeadView,
  duplicateText,
  leadProblem,
  LOST_REASONS,
  LOST_TEXT,
  type LostReason,
  nextStages,
  NOTE_KIND_TEXT,
  NOTE_KINDS,
  type NoteKind,
  noteError,
  PICKED_SOURCES,
  readNewLead,
  readStage,
  type Source,
  SOURCE_TEXT,
  type Stage,
  STAGE_TEXT,
  STAGES,
  stageText,
  storedCallNote,
} from "./leads";
import { todayIn } from "./pickup";
import { isId } from "./route";
import { type Saved, SERVER_PROBLEM, saveFailure } from "./save";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { Skeleton } from "./Skeleton";

const INBOX = "";

function SessionButton({ saved, auth }: { saved: Saved | null; auth: Auth }) {
  if (!saved || saved.ok || !saved.session) return null;
  return (
    <>
      {" "}
      <button type="button" onClick={() => signOut(auth)}>
        Sign in again
      </button>
    </>
  );
}

function useOwners(user: User, enabled: boolean) {
  const [found, setFound] = useState<api.Assignee[] | "failed" | undefined>(undefined);
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    user
      .getIdToken()
      .then((token) => api.leadOwners(token))
      .then((list) => live && setFound(list))
      .catch(() => live && setFound("failed"));
    return () => {
      live = false;
    };
  }, [user, enabled]);
  return found;
}

function OwnerSelect({
  id,
  found,
  value,
  onChange,
}: {
  id: string;
  found: api.Assignee[] | "failed" | undefined;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <select id={id} data-testid={id} value={value} disabled={!Array.isArray(found)} onChange={(e) => onChange(e.target.value)}>
      <option value={INBOX}>{found === "failed" ? "Cannot load the members" : found ? "No owner (the inbox)" : "Loading…"}</option>
      {Array.isArray(found) &&
        found.map((person) => (
          <option key={person.id} value={person.id}>
            {memberName(person.email)}
          </option>
        ))}
    </select>
  );
}

function MatchList({ matches, base }: { matches: api.LeadMatch[]; base: string }) {
  if (!matches.length) return null;
  return (
    <ul className="matches" data-testid="lead-matches">
      {matches.map((match) => (
        <li key={`${match.kind}-${match.id}`} data-testid="lead-match">
          <span className="chip-flag">Possible match</span>{" "}
          <a href={`${base}/${match.kind === "lead" ? "leads" : "accounts"}/${match.id}`} onClick={go}>
            {match.kind === "account" ? `Account ${match.number} ${match.name}` : `Lead ${match.name}`}
          </a>
          {match.address && <span className="sub"> · {match.address}</span>}
        </li>
      ))}
    </ul>
  );
}

/** The new lead form (WF-30). A salesperson owns the new lead; a manager can pick an owner or the inbox. */
export function LeadForm({
  base,
  auth,
  user,
  canAssign,
  preset,
  onSaved,
  onCancel,
}: {
  base: string;
  auth: Auth;
  user: User;
  canAssign: boolean;
  preset: { source: Source; note: string } | null;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const owners = useOwners(user, canAssign);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [source, setSource] = useState<Source>(preset?.source ?? "hand");
  const [owner, setOwner] = useState(INBOX);
  const [previous, setPrevious] = useState("");
  const [onsite, setOnsite] = useState(false);
  const [note, setNote] = useState(preset?.note ?? "");
  const [saved, setSaved] = useState<Saved | null>(null);
  const [badField, setBadField] = useState<LeadField>("name");
  const [made, setMade] = useState<api.LeadDetail | null>(null);
  const [warned, setWarned] = useState<api.LeadMatch[] | null>(null);
  const [saving, setSaving] = useState(false);
  const entryId = useRef<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => nameRef.current?.focus(), []);
  const edit = (next: () => void) => {
    next();
    entryId.current = null;
    setWarned(null);
    setSaved(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = leadProblem(name, address, phone, note);
    if (problem) {
      setBadField(problem.field);
      setSaved({ ok: false, field: true, text: problem.text });
      document.getElementById(`lead-${problem.field}`)?.focus();
      return;
    }
    entryId.current ??= crypto.randomUUID();
    setSaving(true);
    setSaved(null);
    if (warned === null) {
      const place = { name: name.trim(), address: address.trim(), phone: phone.trim() };
      const found = await user
        .getIdToken()
        .then((token) => api.leadMatches(token, place))
        .catch((e: unknown) => {
          console.info("[leads] no match check before the save; the save continues", e);
          return [];
        });
      if (found.length) {
        setWarned(found);
        setSaving(false);
        setSaved({ ok: false, field: false, text: duplicateText(found.length) });
        return;
      }
    }
    try {
      const entry: api.LeadEntry = {
        id: entryId.current,
        name: name.trim(),
        address: address.trim(),
        phone: phone.trim(),
        source,
        owner_id: owner || null,
        previous_collector: previous.trim(),
        competitor_onsite: onsite,
        note: note.trim(),
      };
      const next = await api.makeLead(await user.getIdToken(), entry);
      entryId.current = null;
      setName("");
      setAddress("");
      setPhone("");
      setPrevious("");
      setOnsite(false);
      setNote("");
      setMade(next);
      setWarned(null);
      setSaved({ ok: true, text: `Added the lead ${entry.name}.` });
      onSaved();
      requestAnimationFrame(() => nameRef.current?.focus());
    } catch (e) {
      const failure = saveFailure(e, "The lead");
      const status = e instanceof api.ApiError ? e.status : 0;
      setBadField("name");
      setSaved(!failure.ok && status !== 422 ? { ...failure, field: false } : failure);
    } finally {
      setSaving(false);
    }
  };
  const fieldError = saved && !saved.ok && saved.field ? saved.text : null;
  const errorAt = (field: LeadField) =>
    fieldError && badField === field ? (
      <p id={`lead-${field}-error`} role="alert" className="error" data-testid="lead-error">
        {fieldError}
      </p>
    ) : null;
  const describe = (field: LeadField) =>
    fieldError && badField === field ? { "aria-invalid": true, "aria-describedby": `lead-${field}-error` } : {};
  return (
    <form onSubmit={submit} noValidate className="lead-form" data-testid="lead-form" aria-labelledby="lead-form-title">
      <h2 id="lead-form-title">{preset ? "Add a lead from the call" : "Add a lead"}</h2>
      <label htmlFor="lead-name">Name of the place</label>
      <input ref={nameRef} id="lead-name" data-testid="lead-name" autoComplete="off" value={name} onChange={(e) => edit(() => setName(e.target.value))} {...describe("name")} />
      {errorAt("name")}
      <label htmlFor="lead-address">Address</label>
      <input id="lead-address" data-testid="lead-address" autoComplete="off" value={address} onChange={(e) => edit(() => setAddress(e.target.value))} {...describe("address")} />
      {errorAt("address")}
      <label htmlFor="lead-phone">Phone</label>
      <input id="lead-phone" data-testid="lead-phone" type="tel" autoComplete="off" value={phone} onChange={(e) => edit(() => setPhone(e.target.value))} {...describe("phone")} />
      {errorAt("phone")}
      <label htmlFor="lead-source">Source</label>
      <select id="lead-source" data-testid="lead-source" value={source} onChange={(e) => edit(() => setSource(e.target.value as Source))}>
        {PICKED_SOURCES.map((value) => (
          <option key={value} value={value}>
            {SOURCE_TEXT[value]}
          </option>
        ))}
      </select>
      {canAssign && (
        <>
          <label htmlFor="lead-owner">Owner</label>
          <OwnerSelect id="lead-owner" found={owners} value={owner} onChange={(v) => edit(() => setOwner(v))} />
        </>
      )}
      <label htmlFor="lead-previous">Previous collector (optional)</label>
      <input id="lead-previous" data-testid="lead-previous" autoComplete="off" value={previous} onChange={(e) => edit(() => setPrevious(e.target.value))} />
      <label className="check">
        <input type="checkbox" data-testid="lead-onsite" checked={onsite} onChange={(e) => edit(() => setOnsite(e.target.checked))} />
        A competitor has a container on site
      </label>
      <label htmlFor="lead-note">First note (optional)</label>
      <textarea id="lead-note" data-testid="lead-note" rows={2} value={note} onChange={(e) => edit(() => setNote(e.target.value))} {...describe("note")} />
      {errorAt("note")}
      <div className="actions">
        <button className="primary" data-testid="save-lead" disabled={saving}>
          {saving ? "Saving…" : warned ? "Add the lead anyway" : "Add the lead"}
        </button>
        <button type="button" data-testid="lead-form-cancel" onClick={onCancel}>
          {saved?.ok ? "Close" : "Cancel"}
        </button>
      </div>
      <p role="status" data-testid="lead-status" className={saved && !saved.ok && !saved.field ? "error" : undefined}>
        {saved && !fieldError && saved.text}
        {saved?.ok && made?.lead && (
          <>
            {" "}
            <a href={`${base}/leads/${made.lead.id}`} onClick={go} data-testid="lead-open-new">
              Open the lead
            </a>
          </>
        )}
        <SessionButton saved={saved} auth={auth} />
      </p>
      {warned && <MatchList matches={warned} base={base} />}
      {saved?.ok && made && <MatchList matches={made.matches} base={base} />}
    </form>
  );
}

function LeadTable({
  leads,
  base,
  org,
  onRestore,
}: {
  leads: api.Lead[];
  base: string;
  org: api.Bootstrap;
  onRestore?: (lead: api.Lead) => void;
}) {
  const [sort, setSort] = useSort("sort", LEAD_COLUMNS);
  return (
    <>
      <SortSelect id="leads-select" columns={LEAD_COLUMNS} sort={sort} onSort={setSort} fallback="Changed last first" />
      <div className="table-scroll" tabIndex={0} role="region" aria-label="Leads">
        <table className="list leads-table" data-testid="lead-table" data-cols="leads-select">
          <thead>
            <tr>
              {LEAD_COLUMNS.map((column) => (
                <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
              ))}
            </tr>
          </thead>
          <tbody>
            {sortRows(leads, LEAD_COLUMNS, sort).map((lead) => (
              <tr key={lead.id} data-testid="lead-row" data-stage={lead.stage} className={lead.deleted ? "deleted" : undefined}>
                <th scope="row" title={lead.deleted ? deletedTitle(lead.deleted, org.time_zone) : undefined}>
                  <a href={`${base}/leads/${lead.id}`} onClick={go} data-testid="lead-link">
                    {lead.name}
                  </a>
                  {lead.deleted && onRestore && (
                    <>
                      {" "}
                      <button type="button" className="link" data-testid="lead-row-restore" onClick={() => onRestore(lead)}>
                        Restore
                      </button>
                    </>
                  )}
                </th>
                <td data-label="Stage" title={stageText(lead.stage, lead.lost_reason, lead.lost_note)}>
                  {STAGE_TEXT[lead.stage]}
                </td>
                <td data-label="Owner" title={lead.owner_email ?? "No owner: the lead is in the inbox."}>
                  <span className="clip name">{lead.mine ? "You" : lead.owner_email ? emailName(lead.owner_email) : "Inbox"}</span>
                </td>
                <td data-label="Address" title={lead.address}>
                  <span className="clip">{lead.address || "–"}</span>
                </td>
                <td data-label="Source">{SOURCE_TEXT[lead.source]}</td>
                <td data-label="Changed" title={callTime(lead.updated_at, org.time_zone)}>
                  {shortTime(lead.updated_at, org.time_zone)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

type ListLoad = api.LeadList | "server" | "network" | "session" | "hidden" | undefined;

function writeList(view: LeadView, stage: Stage | null) {
  const url = new URL(window.location.href);
  url.searchParams.set("view", view);
  if (stage) url.searchParams.set("stage", stage);
  else url.searchParams.delete("stage");
  url.searchParams.delete("new");
  window.history.replaceState(window.history.state, "", url);
}

export function LeadList({
  base,
  org,
  auth,
  user,
  canView,
}: {
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  canView: boolean | undefined;
}) {
  const preset = useState(() => readNewLead(window.location.search, storedCallNote()))[0];
  const [view, setView] = useState<LeadView>(() => defaultLeadView(window.location.search, undefined));
  const [stage, setStage] = useState<Stage | null>(() => readStage(window.location.search));
  const [found, setFound] = useState<ListLoad>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [adding, setAdding] = useState(preset !== null);
  const [importing, setImporting] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [restoreStatus, setRestoreStatus] = useState<Saved | null>(null);
  const [mapMode, pickMode] = useMapMode();
  useTitle(`Leads · ${org.name}`);
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    user
      .getIdToken()
      .then((token) => api.leads(token, view, stage, showDeleted))
      .then((list) => {
        if (!live) return;
        setFound(list);
        if (view === "mine" && !list.can_own) setView("all");
      })
      .catch((e) => {
        const code = e instanceof api.ApiError ? e.status : 0;
        if (live) setFound(code === 404 ? "hidden" : code === 401 ? "session" : code >= 500 ? "server" : "network");
      });
    return () => {
      live = false;
    };
  }, [user, view, stage, attempt, canView, showDeleted]);
  const restore = async (lead: api.Lead) => {
    setRestoreStatus(null);
    try {
      await api.restoreLead(await user.getIdToken(), lead.id);
      setRestoreStatus({ ok: true, text: `${lead.name} is back in the lists.` });
      setAttempt((n) => n + 1);
    } catch (e) {
      setRestoreStatus(saveFailure(e, "The restore"));
    }
  };
  const pick = (next: LeadView) => {
    setView(next);
    writeList(next, stage);
  };
  const pickStage = (next: Stage | null) => {
    setStage(next);
    writeList(view, next);
  };
  const closeForm = () => {
    setAdding(false);
    forgetCallNote();
    writeList(view, stage);
  };
  if (canView === false || found === "hidden") {
    return (
      <main data-testid="leads-hidden">
        <h1 tabIndex={-1}>Leads</h1>
        <p>Your role does not show leads.</p>
      </main>
    );
  }
  const list = typeof found === "object" ? found : null;
  const shown = list ? list.leads : [];
  const tabs: [LeadView, string][] = [
    ...(list?.can_own === false ? [] : [["mine", "Mine"] as [LeadView, string]]),
    ["inbox", "Inbox"],
    ["all", "All that I can see"],
  ];
  return (
    <main className="leads-page wide" data-testid="leads-page">
      <h1 tabIndex={-1}>Leads</h1>
      <ViewSwitch map={mapMode} onPick={pickMode} />
      <div className="tabs" role="group" aria-label="Which leads">
        {tabs.map(([value, label]) => (
          <button key={value} type="button" aria-pressed={view === value} data-testid={`leads-${value}`} onClick={() => pick(value)}>
            {label}
          </button>
        ))}
        {list?.can_add && !adding && (
          <button type="button" className="primary" data-testid="lead-add" onClick={() => setAdding(true)}>
            Add a lead
          </button>
        )}
        {list?.can_add && !importing && (
          <button type="button" data-testid="lead-import-open" onClick={() => setImporting(true)}>
            Import a file
          </button>
        )}
        {list?.can_delete && !mapMode && (
          <label className="check">
            <input type="checkbox" data-testid="leads-show-deleted" checked={showDeleted} onChange={(event) => setShowDeleted(event.target.checked)} />
            Show deleted
          </label>
        )}
      </div>
      {restoreStatus && (
        <p role="status" data-testid="leads-restore-status" className={restoreStatus.ok ? undefined : "error"}>
          {restoreStatus.text}
        </p>
      )}
      {list?.can_add && importing && (
        <LeadImport base={base} auth={auth} user={user} onDone={() => setAttempt((n) => n + 1)} onCancel={() => setImporting(false)} />
      )}
      {list?.can_add && adding && (
        <LeadForm
          base={base}
          auth={auth}
          user={user}
          canAssign={list.can_assign}
          preset={preset}
          onSaved={() => {
            forgetCallNote();
            writeList(view, stage);
            setAttempt((n) => n + 1);
          }}
          onCancel={closeForm}
        />
      )}
      {list && (
        <div className="tabs stages" role="group" aria-label="Stage">
          <button type="button" aria-pressed={stage === null} data-testid="stage-all" onClick={() => pickStage(null)}>
            All stages ({Object.values(list.counts).reduce((a, b) => a + b, 0)})
          </button>
          {STAGES.map((value) => (
            <button key={value} type="button" aria-pressed={stage === value} data-testid={`stage-${value}`} onClick={() => pickStage(value)}>
              {STAGE_TEXT[value]} ({list.counts[value]})
            </button>
          ))}
        </div>
      )}
      {found === undefined ? (
        <Skeleton shape="table" columns={5} rows={8} />
      ) : found === "session" ? (
        <p role="alert" className="error">
          Your session ended.{" "}
          <button type="button" onClick={() => signOut(auth)}>
            Sign in again
          </button>
        </p>
      ) : typeof found === "string" ? (
        <p role="alert" className="error">
          {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </p>
      ) : mapMode ? (
        <PinMap kind="leads" filters={leadPinFilters(view, stage)} base={base} auth={auth} user={user} timeZone={org.time_zone} />
      ) : shown.length === 0 ? (
        <p data-testid="leads-none">
          {showDeleted
            ? "No deleted leads here."
            : view === "inbox"
              ? "No lead without an owner is visible to you."
              : view === "mine"
                ? "You own no leads here."
                : "No leads here."}
        </p>
      ) : (
        <>
          {list?.more && (
            <p className="sub" data-testid="leads-more">
              This list shows the {shown.length} leads that changed last. Pick a stage to see others.
            </p>
          )}
          {showDeleted && <p className="sub">Deleted leads. A restore puts a lead back in the lists.</p>}
          <LeadTable leads={shown} base={base} org={org} onRestore={showDeleted ? (lead) => void restore(lead) : undefined} />
        </>
      )}
    </main>
  );
}

function LostForm({ onSave, onCancel, saving }: { onSave: (reason: LostReason, note: string) => void; onCancel: () => void; saving: boolean }) {
  const [reason, setReason] = useState<LostReason | "">("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const choice = useRef<HTMLSelectElement>(null);
  useEffect(() => choice.current?.focus(), []);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!reason) return setError("Choose why the lead was lost.");
    if (reason === "other" && !note.trim()) return setError("The reason Other needs a note.");
    onSave(reason, note.trim());
  };
  return (
    <form onSubmit={submit} noValidate className="step-form" data-testid="lost-form">
      <label htmlFor="lost-reason">Why was the lead lost?</label>
      <select ref={choice} id="lost-reason" data-testid="lost-reason" value={reason} onChange={(e) => setReason(e.target.value as LostReason)}>
        <option value="">Choose a reason</option>
        {LOST_REASONS.map((value) => (
          <option key={value} value={value}>
            {LOST_TEXT[value]}
          </option>
        ))}
      </select>
      <label htmlFor="lost-note">Note{reason === "other" ? "" : " (optional)"}</label>
      <textarea id="lost-note" data-testid="lost-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      {error && (
        <p role="alert" className="error" data-testid="lost-error">
          {error}
        </p>
      )}
      <div className="actions">
        <button className="primary" data-testid="save-lost" disabled={saving}>
          {saving ? "Saving…" : "Mark as lost"}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function NoteForm({ lead, auth, user, onSaved }: { lead: api.Lead; auth: Auth; user: User; onSaved: (next: api.LeadDetail) => void }) {
  const [kind, setKind] = useState<NoteKind>("visit");
  const [minutes, setMinutes] = useState("");
  const [note, setNote] = useState("");
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const entryId = useRef<string | null>(null);
  const edit = (next: () => void) => {
    next();
    entryId.current = null;
    setSaved(null);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = noteError(note, minutes);
    if (problem) {
      setSaved({ ok: false, field: true, text: problem });
      return;
    }
    entryId.current ??= crypto.randomUUID();
    setSaving(true);
    setSaved(null);
    try {
      const entry: api.LeadNoteEntry = { id: entryId.current, kind, minutes: minutes.trim() ? Number(minutes) : null, note: note.trim() };
      const next = await api.addLeadNote(await user.getIdToken(), lead.id, entry);
      entryId.current = null;
      setNote("");
      setMinutes("");
      onSaved(next);
      setSaved({ ok: true, text: `Saved: ${NOTE_KIND_TEXT[entry.kind]}.` });
    } catch (e) {
      setSaved(saveFailure(e, "The note"));
    } finally {
      setSaving(false);
    }
  };
  const fieldError = saved && !saved.ok && saved.field ? saved.text : null;
  return (
    <form onSubmit={submit} noValidate className="lead-form" data-testid="lead-note-form" aria-labelledby="lead-note-title">
      <h3 id="lead-note-title">Add a note</h3>
      <label htmlFor="note-kind">Kind</label>
      <select id="note-kind" data-testid="note-kind" value={kind} onChange={(e) => edit(() => setKind(e.target.value as NoteKind))}>
        {NOTE_KINDS.map((value) => (
          <option key={value} value={value}>
            {NOTE_KIND_TEXT[value]}
          </option>
        ))}
      </select>
      <label htmlFor="note-minutes">Minutes spent (optional)</label>
      <input id="note-minutes" data-testid="note-minutes" inputMode="numeric" value={minutes} onChange={(e) => edit(() => setMinutes(e.target.value))} />
      <label htmlFor="note-text">Note</label>
      <textarea
        id="note-text"
        data-testid="note-text"
        rows={2}
        value={note}
        onChange={(e) => edit(() => setNote(e.target.value))}
        aria-invalid={fieldError ? true : undefined}
        aria-describedby={fieldError ? "note-error" : undefined}
      />
      {fieldError && (
        <p id="note-error" role="alert" className="error" data-testid="note-error">
          {fieldError}
        </p>
      )}
      <div className="actions">
        <button data-testid="save-note" disabled={saving}>
          {saving ? "Saving…" : "Save the note"}
        </button>
      </div>
      <p role="status" data-testid="note-status" className={saved && !saved.ok && !saved.field ? "error" : undefined}>
        {saved && !fieldError && saved.text}
        <SessionButton saved={saved} auth={auth} />
      </p>
    </form>
  );
}

function DetailsForm({ lead, onSave, onCancel, saving }: { lead: api.Lead; onSave: (change: api.LeadChange) => void; onCancel: () => void; saving: boolean }) {
  const [name, setName] = useState(lead.name);
  const [address, setAddress] = useState(lead.address);
  const [phone, setPhone] = useState(lead.phone);
  const [qualifier, setQualifier] = useState(lead.qualifier);
  const [previous, setPrevious] = useState(lead.previous_collector);
  const [onsite, setOnsite] = useState(lead.competitor_onsite);
  const [error, setError] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const problem = leadProblem(name, address, phone.trim() === lead.phone.trim() ? "" : phone, "");
    if (problem) return setError(problem.text);
    onSave({
      name: name.trim(),
      address: address.trim(),
      phone: phone.trim(),
      qualifier: qualifier.trim(),
      previous_collector: previous.trim(),
      competitor_onsite: onsite,
    });
  };
  return (
    <form onSubmit={submit} noValidate className="lead-form" data-testid="lead-details-form">
      <label htmlFor="edit-name">Name of the place</label>
      <input id="edit-name" data-testid="edit-name" value={name} onChange={(e) => setName(e.target.value)} />
      <label htmlFor="edit-address">Address</label>
      <input id="edit-address" data-testid="edit-address" value={address} onChange={(e) => setAddress(e.target.value)} />
      <label htmlFor="edit-phone">Phone</label>
      <input id="edit-phone" data-testid="edit-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
      <label htmlFor="edit-qualifier">Qualifier</label>
      <input id="edit-qualifier" data-testid="edit-qualifier" value={qualifier} onChange={(e) => setQualifier(e.target.value)} />
      <label htmlFor="edit-previous">Previous collector</label>
      <input id="edit-previous" data-testid="edit-previous" value={previous} onChange={(e) => setPrevious(e.target.value)} />
      <label className="check">
        <input type="checkbox" data-testid="edit-onsite" checked={onsite} onChange={(e) => setOnsite(e.target.checked)} />
        A competitor has a container on site
      </label>
      {error && (
        <p role="alert" className="error" data-testid="details-error">
          {error}
        </p>
      )}
      <div className="actions">
        <button className="primary" data-testid="save-details" disabled={saving}>
          {saving ? "Saving…" : "Save the details"}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

type DetailLoad = api.LeadDetail | "missing" | "server" | "network" | "session" | undefined;

export function LeadPage({ id, base, org, auth, user, listAllowed = true }: { id: string; base: string; org: api.Bootstrap; auth: Auth; user: User; listAllowed?: boolean }) {
  const [found, setFound] = useState<DetailLoad>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [lostOpen, setLostOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [owner, setOwner] = useState<string | null>(null);
  const [status, setStatus] = useState<Saved | null>(null);
  const [undo, setUndo] = useState<api.LeadChange | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const statusRef = useRef<HTMLParagraphElement>(null);
  const detail = typeof found === "object" ? found : null;
  const lead = detail?.lead ?? null;
  const owners = useOwners(user, Boolean(detail?.can_assign && lead?.can_edit));
  useTitle(lead ? `${lead.name} · ${org.name}` : `Lead · ${org.name}`);
  useCrumb(lead ? lead.name : null);
  useEffect(() => {
    if (!isId(id)) {
      setFound("missing");
      return;
    }
    let live = true;
    user
      .getIdToken()
      .then((token) => api.lead(token, id))
      .then((next) => live && setFound(next.lead ? next : "missing"))
      .catch((e) => {
        const code = e instanceof api.ApiError ? e.status : 0;
        if (live) setFound(code === 404 ? "missing" : code === 401 ? "session" : code >= 500 ? "server" : "network");
      });
    return () => {
      live = false;
    };
  }, [id, user, attempt]);
  const loaded = found !== undefined;
  useEffect(() => {
    if (loaded && document.activeElement === document.body) document.querySelector<HTMLElement>("main h1")?.focus();
  }, [loaded]);

  const back = (
    <p>
      <a href={listAllowed ? `${base}/leads` : base} onClick={go} className="back" data-testid="back-to-leads">
        {listAllowed ? "← All leads" : "← Home"}
      </a>
    </p>
  );
  if (found === undefined) {
    return (
      <main>
        {back}
        <Skeleton shape="detail" />
      </main>
    );
  }
  if (found === "missing" || !lead || !detail) {
    if (typeof found === "string" && found !== "missing") {
      return (
        <main data-testid="lead-failed">
          {back}
          <h1 tabIndex={-1}>{found === "session" ? "Your session ended" : "Cannot load the lead"}</h1>
          {found === "session" ? (
            <button className="primary" onClick={() => signOut(auth)}>
              Sign in again
            </button>
          ) : (
            <p role="alert" className="error">
              {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
              <button onClick={() => setAttempt((n) => n + 1)}>Try again</button>
            </p>
          )}
        </main>
      );
    }
    return (
      <main data-testid="lead-missing">
        {back}
        <h1 tabIndex={-1}>Lead not found</h1>
        <p>No lead at this link is visible to you.</p>
      </main>
    );
  }
  const change = async (body: api.LeadChange, done: string, back: api.LeadChange | null = null) => {
    setSaving(true);
    setStatus(null);
    setUndo(null);
    try {
      const next = await api.changeLead(await user.getIdToken(), lead.id, body);
      setFound(next.lead ? next : "missing");
      setStatus({ ok: true, text: done });
      setUndo(back);
      setLostOpen(false);
      setEditing(false);
      setVerifying(false);
      setOwner(null);
    } catch (e) {
      setStatus(saveFailure(e, "The change"));
    } finally {
      setSaving(false);
      requestAnimationFrame(() => statusRef.current?.focus());
    }
  };
  const stages = lead.can_edit ? nextStages(lead.stage) : [];
  const stageBack: api.LeadChange =
    lead.stage === "lost" && lead.lost_reason ? { stage: "lost", lost_reason: lead.lost_reason, lost_note: lead.lost_note } : { stage: lead.stage };
  const ownerText = lead.mine ? "You" : lead.owner_email ? memberName(lead.owner_email) : "No owner: the lead is in the inbox";
  return (
    <main className="lead-page" data-testid="lead-page" data-stage={lead.stage} data-today={todayIn(org.time_zone)}>
      {back}
      <h1 tabIndex={-1}>{lead.name}</h1>
      <DeletedBanner kind="lead" deleted={lead.deleted} timeZone={org.time_zone} />
      <dl className="facts" data-testid="lead-facts">
        <dt>Stage</dt>
        <dd data-testid="lead-stage">{stageText(lead.stage, lead.lost_reason, lead.lost_note)}</dd>
        <dt>Owner</dt>
        <dd data-testid="lead-owner-text">{ownerText}</dd>
        <dt>Address</dt>
        <dd>{lead.address || "–"}</dd>
        <dt>Phone</dt>
        <dd>{lead.phone ? <a href={`tel:${lead.phone}`}>{lead.phone}</a> : "–"}</dd>
        <dt>Source</dt>
        <dd>{SOURCE_TEXT[lead.source]}</dd>
        <dt>Qualifier</dt>
        <dd>{lead.qualifier || "–"}</dd>
        <dt>Previous collector</dt>
        <dd>
          {lead.previous_collector || "–"}
          {lead.competitor_onsite && " · a competitor has a container on site"}
        </dd>
        <dt>Verified</dt>
        <dd data-testid="lead-verified">
          {lead.verified_at ? `${callTime(lead.verified_at, org.time_zone)} by ${lead.verified_email ? memberName(lead.verified_email) : "a removed member"}` : "Not yet"}
        </dd>
        <dt>Added</dt>
        <dd>{callTime(lead.created_at, org.time_zone)}</dd>
      </dl>
      <LocationCard
        door={lead.door}
        hasTank={false}
        canDoor={lead.can_edit}
        canTank={false}
        address={lead.address}
        timeZone={org.time_zone}
        user={user}
        save={async (change) => {
          const next = await api.setLeadLocation(await user.getIdToken(), lead.id, change);
          setFound(next.lead ? next : "missing");
        }}
      />
      <LeadDeal lead={lead} detail={detail} base={base} user={user} />
      <MatchList matches={detail.matches} base={base} />
      <p ref={statusRef} tabIndex={-1} role="status" data-testid="lead-change-status" className={status && !status.ok ? "error" : undefined}>
        {status?.text}
        {status?.ok && undo && (
          <>
            {" "}
            <button type="button" data-testid="lead-undo" disabled={saving} onClick={() => void change(undo, `Undone: the lead is ${STAGE_TEXT[undo.stage ?? "lead"].toLowerCase()} again.`)}>
              Undo
            </button>
          </>
        )}
        <SessionButton saved={status} auth={auth} />
      </p>
      {lead.can_edit && (
        <section aria-labelledby="lead-actions" data-testid="lead-actions">
          <h2 id="lead-actions">Change</h2>
          {stages.length > 0 && !lostOpen && (
            <div className="actions" role="group" aria-label="Move to the stage">
              {stages.map((to) => (
                <button
                  key={to}
                  type="button"
                  data-testid={`lead-to-${to}`}
                  disabled={saving}
                  onClick={() => (to === "lost" ? setLostOpen(true) : void change({ stage: to }, `The lead is now ${STAGE_TEXT[to].toLowerCase()}.`, stageBack))}
                >
                  {to === "lost" ? "Mark as lost…" : `Move to ${STAGE_TEXT[to].toLowerCase()}`}
                </button>
              ))}
            </div>
          )}
          {lostOpen && (
            <LostForm
              saving={saving}
              onCancel={() => setLostOpen(false)}
              onSave={(reason, note) => void change({ stage: "lost", lost_reason: reason, lost_note: note }, `The lead is lost: ${LOST_TEXT[reason]}.`, stageBack)}
            />
          )}
          <div className="actions">
            {verifying ? (
              <span role="group" aria-label="Verify the lead">
                Verify that you visited or called {lead.name}? You cannot remove a verification.{" "}
                <button type="button" className="primary" data-testid="lead-verify-confirm" disabled={saving} autoFocus onClick={() => void change({ verify: true }, "Verified: you visited or called the place.")}>
                  Verify
                </button>{" "}
                <button type="button" data-testid="lead-verify-keep" onClick={() => setVerifying(false)}>
                  Cancel
                </button>
              </span>
            ) : (
              <button type="button" data-testid="lead-verify" disabled={saving} onClick={() => setVerifying(true)}>
                Verify (visited or called)
              </button>
            )}
            {!editing && (
              <button type="button" data-testid="lead-edit" onClick={() => setEditing(true)}>
                Change the details
              </button>
            )}
          </div>
          {editing && <DetailsForm lead={lead} saving={saving} onCancel={() => setEditing(false)} onSave={(body) => void change(body, "Saved the details.")} />}
          {detail.can_assign && (
            <form
              className="lead-form owner-form"
              data-testid="lead-owner-form"
              onSubmit={(event) => {
                event.preventDefault();
                if (owner === null) return;
                const picked = Array.isArray(owners) ? owners.find((person) => person.id === owner)?.email : undefined;
                const who = picked ? memberName(picked) : undefined;
                void change(owner ? { owner_id: owner } : { clear_owner: true }, owner ? `The owner is now ${who ?? "the member"}.` : "The lead is in the inbox.");
              }}
            >
              <label htmlFor="lead-owner-change">Owner</label>
              <OwnerSelect id="lead-owner-change" found={owners} value={owner ?? lead.owner_id ?? INBOX} onChange={setOwner} />
              <div className="actions">
                <button data-testid="save-owner" disabled={saving || owner === null || owner === (lead.owner_id ?? INBOX)}>
                  Save the owner
                </button>
              </div>
            </form>
          )}
          <NoteForm lead={lead} auth={auth} user={user} onSaved={(next) => setFound(next.lead ? next : "missing")} />
        </section>
      )}
      <LeadFollowUps lead={lead} items={detail.follow_ups} org={org} auth={auth} user={user} onSaved={(next) => setFound(next.lead ? next : "missing")} />
      <section aria-labelledby="lead-notes" data-testid="lead-notes">
        <h2 id="lead-notes">Notes</h2>
        {detail.notes.length ? (
          <ul className="calls">
            {detail.notes.map((note) => (
              <li key={note.id} data-testid="lead-note-row" data-day={callDay(note.at, org.time_zone)}>
                <p>
                  <strong>{NOTE_KIND_TEXT[note.kind]}</strong>
                  {note.minutes !== null && ` · ${note.minutes} min`}
                  <span className="sub">
                    {" "}
                    · {note.by_email ? memberName(note.by_email) : "a removed member"} · {callTime(note.at, org.time_zone)}
                  </span>
                </p>
                <p>{note.note}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p>No notes yet.</p>
        )}
      </section>
      <DeleteRecord
        kind="lead"
        names={[lead.name]}
        deleted={lead.deleted}
        canDelete={Boolean(detail.can_delete)}
        canPurge={Boolean(detail.can_purge)}
        onDelete={async (reason) => {
          const next = await api.deleteLead(await user.getIdToken(), lead.id, reason);
          setFound(next.lead ? next : "missing");
        }}
        onRestore={async () => {
          const next = await api.restoreLead(await user.getIdToken(), lead.id);
          setFound(next.lead ? next : "missing");
        }}
        onPurge={async (confirm) => {
          await api.purgeLead(await user.getIdToken(), lead.id, confirm);
          navigate(listAllowed ? `${base}/leads` : base);
        }}
      />
    </main>
  );
}
